import { ChangeEvent } from "react";
import { Box, TablePagination, TableSortLabel, Tooltip, Typography } from "@mui/material";
import { formatCurrency, formatDisplayDate } from "../../formatters";
import {
  BRAND_STAGES,
  BrandStageLeadRow,
  BrandStageLeadSort,
  BrandStageLeadSortKey,
} from "../brandStagesApiMapping";

// Column widths from spec Section 3.5 - the All-brands drill-down adds the
// Brand Name column; a brand-specific one omits it (the brand is already
// in the subtitle).
const ALL_BRANDS_COLUMNS = "56px minmax(220px,1.3fr) minmax(150px,1fr) 160px 96px 170px";
const SINGLE_BRAND_COLUMNS = "56px minmax(240px,1.4fr) 170px 100px 180px";
export const DRILL_DOWN_PAGE_SIZE_OPTIONS = [10, 25, 100];

const headerCellSx = { p: "14px 8px", fontSize: 12.5, fontWeight: 700, color: "#212121" };

function formatOptionalDate(value: string | null): string {
  return value ? formatDisplayDate(value) : "—";
}

interface BrandStagesDrillDownTableProps {
  rows: BrandStageLeadRow[];
  showBrand: boolean;
  sort: BrandStageLeadSort;
  onSortChange: (sort: BrandStageLeadSort) => void;
  page: number;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

// Leads that reached the drilled stage, already sorted by the caller.
// Column headers sort (default reported_at DESC); paginated client-side;
// no row action column.
export default function BrandStagesDrillDownTable({
  rows,
  showBrand,
  sort,
  onSortChange,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
}: BrandStagesDrillDownTableProps) {
  const gridTemplateColumns = showBrand ? ALL_BRANDS_COLUMNS : SINGLE_BRAND_COLUMNS;
  const pageRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderSortableHeader = (label: string, key: BrandStageLeadSortKey, alignRight = false) => (
    <Box sx={{ ...headerCellSx, textAlign: alignRight ? "right" : "left" }}>
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key ? sort.direction : "asc"}
        onClick={() =>
          onSortChange({ key, direction: sort.key === key && sort.direction === "asc" ? "desc" : "asc" })
        }
        sx={{ flexDirection: alignRight ? "row-reverse" : "row" }}
      >
        {label}
      </TableSortLabel>
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: "#fff", border: "1px solid #ececec", borderRadius: "10px", overflow: "hidden" }}>
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: 720 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns,
              alignItems: "center",
              borderBottom: "1px solid #ececec",
              px: 1,
              backgroundColor: "#fcfbfd",
            }}
          >
            {renderSortableHeader("ID", "id")}
            {renderSortableHeader("Business / User Unique ID", "businessName")}
            {showBrand && renderSortableHeader("Brand Name", "brandName")}
            {/* Sorts by reported date - the spec's default sort column. */}
            {renderSortableHeader("Acquired / Reported", "reportedAt")}
            {renderSortableHeader("Income", "income", true)}
            {renderSortableHeader("Stages", "stageIndex")}
          </Box>

          {pageRows.map((row) => (
            <Box
              key={row.id}
              sx={{
                display: "grid",
                gridTemplateColumns,
                alignItems: "center",
                borderBottom: "1px solid #f2f2f2",
                px: 1,
              }}
            >
              <Box sx={{ p: "12px 8px", fontSize: 13.5, color: "#616161" }}>{row.id}</Box>
              <Box sx={{ p: "12px 8px", minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, color: "#212121", fontWeight: 500, overflowWrap: "anywhere" }}>
                  {row.businessName || "—"}
                </Typography>
                <Typography
                  sx={{ fontSize: 11.5, color: "#9e9e9e", fontFamily: "'Roboto Mono', monospace", overflowWrap: "anywhere" }}
                >
                  {row.userUniqueId}
                </Typography>
              </Box>
              {showBrand && (
                <Box sx={{ p: "12px 8px", fontSize: 13.5, color: "#424242", lineHeight: 1.25 }}>{row.brandName}</Box>
              )}
              <Box sx={{ p: "12px 8px" }}>
                <Typography sx={{ fontSize: 13, color: "#424242" }}>{formatOptionalDate(row.acquiredAt)}</Typography>
                <Typography sx={{ fontSize: 11.5, color: "#9e9e9e" }}>rep. {formatOptionalDate(row.reportedAt)}</Typography>
              </Box>
              <Box sx={{ p: "12px 8px", fontSize: 13.5, fontWeight: 500, textAlign: "right", color: "#2E7D32" }}>
                {formatCurrency(row.income)}
              </Box>
              <Box sx={{ p: "12px 8px" }}>
                <Typography sx={{ fontSize: 12.5, color: "#212121", fontWeight: 500 }}>
                  {BRAND_STAGES[row.stageIndex].name}
                </Typography>
                <Box sx={{ display: "flex", gap: "3px", mt: "5px" }}>
                  {BRAND_STAGES.slice(1).map((stage, offset) => {
                    const reached = offset + 1 <= row.stageIndex;
                    return (
                      <Tooltip key={stage.step} title={`${stage.name} ${reached ? "reached" : "not reached"}`}>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            width: 30,
                            height: 5,
                            borderRadius: "3px",
                            backgroundColor: reached ? stage.color : "#e8e8ec",
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {rows.length === 0 && (
        <Box sx={{ p: 6, textAlign: "center", color: "#9e9e9e", fontSize: 15 }}>
          No leads match this stage in the selected range.
        </Box>
      )}

      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_event, nextPage) => onPageChange(nextPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={DRILL_DOWN_PAGE_SIZE_OPTIONS}
        onRowsPerPageChange={(event: ChangeEvent<HTMLInputElement>) => onRowsPerPageChange(Number(event.target.value))}
        sx={{ borderTop: "1px solid #f2f2f2" }}
      />
    </Box>
  );
}
